import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Upload, Star, Package, User as UserIcon, Bell } from 'lucide-react';
import LeadDetailModal from '@/components/LeadDetailModal';
import TemplateEditModal from '@/components/TemplateEditModal';
import NotificationPreferencesModal from '@/components/NotificationPreferencesModal';
import PackageModal from '@/components/PackageModal';
import PackagesTable from '@/components/ops/PackagesTable';
import AppShell from '@/components/ops/AppShell';
import LeadsTable from '@/components/ops/LeadsTable';
import KeepsakesGrid from '@/components/ops/KeepsakesGrid';
import TemplatesTable from '@/components/ops/TemplatesTable';
import TeamTable from '@/components/ops/TeamTable';
import OpsModal from '@/components/ops/OpsModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } });

/* ────── User Modal ────── */
function UserModal({ user: editUser, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: editUser?.name || '', email: editUser?.email || '', password: '',
    roles: editUser?.roles || (editUser?.role ? [editUser.role] : ['interviewer']),
    active: editUser?.active ?? true,
    is_production_lead: editUser?.is_production_lead ?? false,
  });
  const [saving, setSaving] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const isEdit = !!editUser;

  const toggleRole = (r) => {
    setForm(f => {
      const has = f.roles.includes(r);
      const nextRoles = has ? f.roles.filter(x => x !== r) : [...f.roles, r];
      return {
        ...f,
        roles: nextRoles,
        is_production_lead: nextRoles.includes('production') ? f.is_production_lead : false,
      };
    });
  };

  const handleSave = async () => {
    if (!form.name || !form.email || (!isEdit && !form.password)) { toast.error('Name, email, and password required'); return; }
    if (!form.roles || form.roles.length === 0) { toast.error('At least one role is required'); return; }
    setSaving(true);
    try {
      const payload = { name: form.name, email: form.email, password: form.password, roles: form.roles, active: form.active, is_production_lead: form.is_production_lead };
      if (!payload.roles.includes('production')) payload.is_production_lead = false;
      if (isEdit && !payload.password) delete payload.password;
      if (isEdit) await axios.put(`${API}/ops/users/${editUser._id}`, payload, getAuth());
      else await axios.post(`${API}/ops/users`, payload, getAuth());
      toast.success(isEdit ? 'User updated' : 'User created');
      onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const ROLE_DEFS = [
    { value: 'admin',       label: 'Admin',       help: 'Full system access' },
    { value: 'interviewer', label: 'Interviewer', help: 'Manages assigned leads and sends emails' },
    { value: 'production',  label: 'Production',  help: 'Receives production jobs and tasks' },
  ];

  const footer = (
    <div className="flex items-center justify-between gap-2">
      <div>
        {isEdit && (
          <button
            onClick={() => setShowPrefs(true)}
            data-testid="user-modal-notification-prefs"
            className="h-9 text-[13px] text-slate-700 hover:text-slate-900 hover:bg-slate-100 border border-slate-200 rounded-md px-3 inline-flex items-center gap-1.5 transition-colors"
          >
            <Bell size={12} className="text-slate-500" /> Notification preferences
          </button>
        )}
      </div>
      <div className="flex items-center gap-2">
        <button onClick={onClose} className="h-9 text-[13px] text-slate-700 hover:text-slate-900 hover:bg-slate-100 border border-slate-200 rounded-md px-3 transition-colors">Cancel</button>
        <button
          onClick={handleSave}
          disabled={saving}
          data-testid="save-user-button"
          className="h-9 bg-slate-900 hover:bg-slate-800 text-white px-4 rounded-md text-[13px] font-medium shadow-sm disabled:opacity-50 transition-colors"
        >
          {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create user'}
        </button>
      </div>
    </div>
  );

  return (
    <OpsModal
      title={isEdit ? 'Edit user' : 'Add user'}
      subtitle={isEdit ? editUser.email : 'Required fields are marked *'}
      icon={UserIcon}
      onClose={onClose}
      size="sm"
      footer={footer}
      testid="user-modal"
    >
      <div className="px-5 py-4 space-y-4">

        <div className="grid grid-cols-1 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider font-medium text-slate-500 block mb-1">Name *</label>
            <input
              value={form.name}
              onChange={e => setForm({...form, name: e.target.value})}
              data-testid="user-name-input"
              className="w-full h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-2 text-[13px] text-slate-900 outline-none transition-colors"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider font-medium text-slate-500 block mb-1">Email *</label>
            <input
              type="email"
              value={form.email}
              onChange={e => setForm({...form, email: e.target.value})}
              data-testid="user-email-input"
              className="w-full h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-2 text-[13px] text-slate-900 outline-none transition-colors"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider font-medium text-slate-500 block mb-1">
              Password {isEdit ? <span className="font-normal normal-case text-slate-400">· leave blank to keep current</span> : '*'}
            </label>
            <input
              type="password"
              value={form.password}
              onChange={e => setForm({...form, password: e.target.value})}
              data-testid="user-password-input"
              className="w-full h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-2 text-[13px] text-slate-900 outline-none transition-colors"
            />
          </div>
        </div>

        {/* Roles */}
        <div>
          <p className="text-[11px] uppercase tracking-wider font-medium text-slate-500 mb-1.5">
            Roles * <span className="font-normal normal-case text-slate-400">· select one or more</span>
          </p>
          <div className="space-y-1.5" data-testid="user-roles-group">
            {ROLE_DEFS.map(r => {
              const checked = form.roles.includes(r.value);
              return (
                <label
                  key={r.value}
                  data-testid={`user-role-${r.value}`}
                  className={`flex items-start gap-2.5 cursor-pointer rounded-md border px-2.5 py-2 transition-colors ${checked ? 'border-slate-900 bg-slate-50' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleRole(r.value)}
                    className="mt-0.5 w-3.5 h-3.5 accent-slate-900"
                    data-testid={`user-role-${r.value}-checkbox`}
                  />
                  <span className="min-w-0">
                    <span className="text-[13px] text-slate-900 block leading-tight">{r.label}</span>
                    <span className="text-[11px] text-slate-500 block mt-0.5 leading-snug">{r.help}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        {/* Production Lead — only when production is checked */}
        {form.roles.includes('production') && (
          <label
            data-testid="production-lead-row"
            className={`flex items-start gap-2.5 cursor-pointer rounded-md border px-2.5 py-2 transition-colors ${form.is_production_lead ? 'border-amber-200 bg-amber-50' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
          >
            <input
              type="checkbox"
              checked={form.is_production_lead}
              onChange={e => setForm({...form, is_production_lead: e.target.checked})}
              data-testid="user-production-lead-checkbox"
              className="mt-0.5 w-3.5 h-3.5 accent-slate-900"
            />
            <span className="min-w-0">
              <span className="text-[13px] text-slate-900 font-medium inline-flex items-center gap-1 leading-tight">
                <Star size={11} className="text-amber-600" /> Production lead
              </span>
              <span className="text-[11px] text-slate-500 block mt-0.5 leading-snug">
                Receives new production job notifications. Only one user holds this role at a time.
              </span>
            </span>
          </label>
        )}

        {/* Active */}
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.active}
            onChange={e => setForm({...form, active: e.target.checked})}
            data-testid="user-active-checkbox"
            className="w-3.5 h-3.5 accent-slate-900"
          />
          <span className="text-[13px] text-slate-900">Active</span>
          <span className="text-[11px] text-slate-500">· inactive users cannot sign in</span>
        </label>
      </div>
      {showPrefs && (
        <NotificationPreferencesModal
          onClose={() => setShowPrefs(false)}
          targetUserId={editUser?._id}
          targetUserName={editUser?.name}
        />
      )}
    </OpsModal>
  );
}

/* ────── Add Lead Modal ────── */
function AddLeadModal({ users, onClose, onSaved }) {
  const [form, setForm] = useState({
    client_first_name: '', client_last_name: '', client_email: '', client_phone: '',
    loved_one_first_name: '', loved_one_pronouns: '', source: '', city: '', state: '',
    full_address: '', initial_contact_date: '', owner: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.client_first_name || !form.client_last_name) { toast.error('Client name required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, owner: form.owner || null };
      await axios.post(`${API}/ops/leads`, payload, getAuth());
      toast.success('Lead created');
      onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const footer = (
    <div className="flex items-center justify-end gap-2">
      <button onClick={onClose} className="h-9 text-[13px] text-slate-700 hover:text-slate-900 hover:bg-slate-100 border border-slate-200 rounded-md px-3 transition-colors">Cancel</button>
      <button
        onClick={handleSave}
        disabled={saving}
        data-testid="save-lead-button"
        className="h-9 bg-slate-900 hover:bg-slate-800 text-white px-4 rounded-md text-[13px] font-medium shadow-sm disabled:opacity-50 transition-colors"
      >
        {saving ? 'Saving…' : 'Create lead'}
      </button>
    </div>
  );

  return (
    <OpsModal
      title="Add lead"
      subtitle="Required fields are marked *"
      icon={Plus}
      onClose={onClose}
      size="md"
      footer={footer}
      testid="add-lead-modal"
    >
      <div className="px-5 py-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            ['client_first_name','First name *'],
            ['client_last_name','Last name *'],
            ['client_email','Email'],
            ['client_phone','Phone'],
            ['loved_one_first_name',"Loved one's name"],
            ['loved_one_pronouns','Pronouns'],
            ['source','Source'],
            ['city','City'],
            ['state','State'],
            ['initial_contact_date','Contact date'],
          ].map(([k, l]) => (
            <div key={k}>
              <label className="text-[11px] uppercase tracking-wider font-medium text-slate-500 block mb-1">{l}</label>
              <input
                value={form[k]}
                onChange={e => set(k, e.target.value)}
                data-testid={`add-lead-${k}`}
                type={k === 'initial_contact_date' ? 'date' : 'text'}
                className="w-full h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-2 text-[13px] text-slate-900 outline-none transition-colors"
              />
            </div>
          ))}
          <div className="sm:col-span-2">
            <label className="text-[11px] uppercase tracking-wider font-medium text-slate-500 block mb-1">Address</label>
            <input
              value={form.full_address}
              onChange={e => set('full_address', e.target.value)}
              data-testid="add-lead-full_address"
              className="w-full h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-2 text-[13px] text-slate-900 outline-none transition-colors"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[11px] uppercase tracking-wider font-medium text-slate-500 block mb-1">Assign to</label>
            <select
              value={form.owner}
              onChange={e => set('owner', e.target.value)}
              data-testid="lead-owner-select"
              className="w-full h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-2 text-[13px] text-slate-700 outline-none transition-colors"
            >
              <option value="">Unassigned</option>
              {users.filter(u => (u.roles || [u.role]).includes('interviewer') && u.active !== false).map(u => (
                <option key={u._id} value={u._id}>{u.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </OpsModal>
  );
}

/* ────── Keepsake Modal ────── */
function KeepsakeModal({ item: editItem, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: editItem?.name || '',
    description: editItem?.description || '',
    photo_url: editItem?.photo_url || '',
    is_active: editItem?.is_active ?? true,
    is_always_included: editItem?.is_always_included ?? false,
    display_order: editItem?.display_order ?? 99,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const isEdit = !!editItem;

  const handleUpload = async (file) => {
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) { toast.error('Photo must be JPG, PNG, or WebP'); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error('Photo must be under 8MB'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/upload`, formData, { headers: { ...getAuth().headers, 'Content-Type': 'multipart/form-data' } });
      // Backend returns path; convert to full URL via /api/files/{path}
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.path}`;
      setForm(f => ({ ...f, photo_url: fullUrl }));
      toast.success('Photo uploaded');
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, display_order: Number(form.display_order) || 99 };
      if (isEdit) await axios.put(`${API}/ops/keepsakes/${editItem.id}`, payload, getAuth());
      else await axios.post(`${API}/ops/keepsakes`, payload, getAuth());
      toast.success(isEdit ? 'Keepsake updated' : 'Keepsake created');
      onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  // Drag-and-drop support
  const [isDragging, setIsDragging] = useState(false);
  const onDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const onDragLeave = () => setIsDragging(false);
  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleUpload(f);
  };

  const footer = (
    <div className="flex items-center justify-end gap-2">
      <button onClick={onClose} className="h-9 text-[13px] text-slate-700 hover:text-slate-900 hover:bg-slate-100 border border-slate-200 rounded-md px-3 transition-colors">Cancel</button>
      <button
        onClick={handleSave}
        disabled={saving || uploading}
        data-testid="save-keepsake-button"
        className="h-9 bg-slate-900 hover:bg-slate-800 text-white px-4 rounded-md text-[13px] font-medium shadow-sm disabled:opacity-50 transition-colors"
      >
        {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create keepsake'}
      </button>
    </div>
  );

  return (
    <OpsModal
      title={isEdit ? 'Edit keepsake' : 'Add keepsake'}
      subtitle={isEdit ? editItem.name : 'Required fields are marked *'}
      icon={Package}
      onClose={onClose}
      size="md"
      footer={footer}
      testid="keepsake-modal"
    >
      <div className="px-5 py-4 space-y-4">

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className="text-[11px] uppercase tracking-wider font-medium text-slate-500 block mb-1">Name *</label>
            <input
              value={form.name}
              onChange={e => setForm({...form, name: e.target.value})}
              data-testid="keepsake-name-input"
              className="w-full h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-2 text-[13px] text-slate-900 outline-none transition-colors"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[11px] uppercase tracking-wider font-medium text-slate-500 block mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={e => setForm({...form, description: e.target.value})}
              rows={3}
              data-testid="keepsake-description-input"
              className="w-full bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-2 py-1.5 text-[13px] text-slate-900 outline-none resize-none leading-relaxed transition-colors"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider font-medium text-slate-500 block mb-1">Display order</label>
            <input
              type="number"
              value={form.display_order}
              onChange={e => setForm({...form, display_order: e.target.value})}
              data-testid="keepsake-order-input"
              className="w-full h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-2 text-[13px] text-slate-900 outline-none transition-colors"
            />
          </div>
        </div>

        {/* Photo */}
        <div>
          <p className="text-[11px] uppercase tracking-wider font-medium text-slate-500 mb-1.5">Photo</p>
          {form.photo_url ? (
            <div className="flex items-start gap-3">
              <div className="aspect-[4/5] w-28 bg-slate-100 rounded-md overflow-hidden border border-slate-200 shrink-0">
                <img
                  src={form.photo_url}
                  alt=""
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="h-8 inline-flex items-center gap-1.5 cursor-pointer text-[12px] text-slate-700 hover:text-slate-900 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-md px-2.5 font-medium transition-colors" data-testid="replace-photo-button">
                  <Upload size={11} /> {uploading ? 'Uploading…' : 'Replace photo'}
                  <input type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={e => handleUpload(e.target.files?.[0])} />
                </label>
                <button
                  type="button"
                  onClick={() => setForm({...form, photo_url: ''})}
                  data-testid="remove-photo-button"
                  className="h-8 text-[12px] text-red-700 hover:text-red-800 hover:bg-red-50 border border-slate-200 hover:border-red-200 rounded-md px-2.5 font-medium transition-colors"
                >
                  Remove
                </button>
              </div>
            </div>
          ) : (
            <label
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              data-testid="upload-photo-zone"
              className={`block rounded-md border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${isDragging ? 'border-slate-900 bg-slate-50' : 'border-slate-300 hover:border-slate-400 bg-white'}`}
            >
              <Upload size={16} className="mx-auto text-slate-400 mb-2" />
              <p className="text-[13px] text-slate-700 font-medium leading-tight">
                {uploading ? 'Uploading…' : 'Drag a photo here or click to upload'}
              </p>
              <p className="text-[11px] text-slate-500 mt-1">JPG, PNG or WebP · max 8MB</p>
              <input type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={e => handleUpload(e.target.files?.[0])} />
            </label>
          )}
          <input
            value={form.photo_url}
            onChange={e => setForm({...form, photo_url: e.target.value})}
            placeholder="Or paste a URL"
            data-testid="keepsake-photo-input"
            className="mt-2 w-full h-7 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-2 text-[12px] text-slate-700 outline-none placeholder:text-slate-400 transition-colors"
          />
        </div>

        {/* Toggles */}
        <div className="space-y-1.5">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={e => setForm({...form, is_active: e.target.checked})}
              data-testid="keepsake-active-checkbox"
              className="w-3.5 h-3.5 accent-slate-900"
            />
            <span className="text-[13px] text-slate-900">Active</span>
            <span className="text-[11px] text-slate-500">· visible to clients</span>
          </label>
          <label
            className={`flex items-start gap-2.5 cursor-pointer rounded-md border px-2.5 py-2 transition-colors ${form.is_always_included ? 'border-slate-900 bg-slate-50' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
          >
            <input
              type="checkbox"
              checked={form.is_always_included}
              onChange={e => setForm({...form, is_always_included: e.target.checked})}
              data-testid="keepsake-always-checkbox"
              className="w-3.5 h-3.5 mt-0.5 accent-slate-900"
            />
            <span className="min-w-0">
              <span className="text-[13px] text-slate-900 font-medium block leading-tight">Always included</span>
              <span className="text-[11px] text-slate-500 block mt-0.5 leading-snug">Shown to every client and not counted toward the 2–4 selection.</span>
            </span>
          </label>
        </div>
      </div>
    </OpsModal>
  );
}

/* ────── Main Admin Dashboard ────── */
export default function OpsAdmin() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Resolve initial tab from URL: /ops/admin/<segment>
  const seg = location.pathname.split('/').pop() || 'leads';
  const URL_TAB_MAP = { leads: 'leads', unassigned: 'unassigned', keepsakes: 'keepsakes', templates: 'templates', packages: 'packages', team: 'team' };
  const [tab, setTab] = useState(URL_TAB_MAP[seg] || 'leads');
  // Keep tab in sync with URL changes (sidebar navigation)
  useEffect(() => {
    const next = URL_TAB_MAP[location.pathname.split('/').pop()];
    if (next && next !== tab) setTab(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  const [users, setUsers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [stages, setStages] = useState([]);
  const [keepsakes, setKeepsakes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showAddLead, setShowAddLead] = useState(false);
  const [showKeepsakeModal, setShowKeepsakeModal] = useState(false);
  const [editingKeepsake, setEditingKeepsake] = useState(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [packages, setPackages] = useState([]);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [stageFilter, setStageFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const csvRef = useRef(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/ops/login'); return; }
    if (!user.roles?.includes('admin')) { navigate('/ops/login'); return; }
  }, [user, authLoading, navigate]);

  const fetchAll = useCallback(async () => {
    try {
      const [usersRes, leadsRes, stagesRes, keepsakesRes, templatesRes, packagesRes] = await Promise.all([
        axios.get(`${API}/ops/users`, getAuth()),
        axios.get(`${API}/ops/leads`, getAuth()),
        axios.get(`${API}/ops/stages`, getAuth()),
        axios.get(`${API}/ops/keepsakes`, getAuth()),
        axios.get(`${API}/ops/email-templates`, getAuth()),
        axios.get(`${API}/ops/stripe-packages`, getAuth()),
      ]);
      setUsers(usersRes.data);
      setLeads(leadsRes.data);
      setStages(stagesRes.data);
      setKeepsakes(keepsakesRes.data);
      setTemplates(templatesRes.data);
      setPackages(packagesRes.data);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user?.roles?.includes('admin')) fetchAll(); }, [user, fetchAll]);

  const handleCsvImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await axios.post(`${API}/ops/leads/import`, formData, { ...getAuth(), headers: { ...getAuth().headers, 'Content-Type': 'multipart/form-data' } });
      toast.success(`Imported ${data.imported} leads${data.errors.length ? ` (${data.errors.length} errors)` : ''}`);
      if (data.errors.length) data.errors.slice(0, 3).forEach(e => toast.error(e));
      fetchAll();
    } catch { toast.error('Import failed'); }
    e.target.value = '';
  };

  if (authLoading || loading) return <div className="min-h-screen flex items-center justify-center bg-[#FAFBFC]"><div className="w-7 h-7 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin" /></div>;

  const interviewers = users.filter(u => (u.roles || [u.role]).includes('interviewer'));
  const unassigned = leads.filter(l => !l.owner);
  const getUserName = (id) => users.find(u => u._id === id)?.name || 'Unassigned';

  // Filter leads for the Leads view
  let filteredLeads = leads;
  if (tab === 'unassigned') filteredLeads = unassigned;
  if (stageFilter) filteredLeads = filteredLeads.filter(l => l.current_stage === stageFilter);
  if (ownerFilter) filteredLeads = filteredLeads.filter(l => ownerFilter === 'unassigned' ? !l.owner : l.owner === ownerFilter);
  if (searchTerm) {
    const s = searchTerm.toLowerCase();
    filteredLeads = filteredLeads.filter(l =>
      `${l.client_first_name} ${l.client_last_name}`.toLowerCase().includes(s) ||
      (l.loved_one_first_name || '').toLowerCase().includes(s) ||
      (l.client_email || '').toLowerCase().includes(s)
    );
  }

  // Sidebar key = currently-active nav item.
  // Map internal "tab" state onto sidebar keys; Unassigned is a sub-view of Leads.
  const navKey = (tab === 'unassigned') ? 'leads' : tab;
  const handleSidebarNav = (key) => {
    setStageFilter(''); setOwnerFilter('');
    const TAB_TO_URL = { leads: '/ops/admin/leads', unassigned: '/ops/admin/unassigned', keepsakes: '/ops/admin/keepsakes', templates: '/ops/admin/templates', team: '/ops/admin/team' };
    const url = TAB_TO_URL[key];
    if (url) navigate(url); else setTab(key);
  };

  // Topbar title + optional subtitle per tab
  const PAGE_META = {
    leads:     { title: 'Leads',           subtitle: `${leads.length} total · ${unassigned.length} unassigned` },
    templates: { title: 'Email Templates', subtitle: `${templates.length} templates` },
    keepsakes: { title: 'Keepsakes',       subtitle: `${keepsakes.length} items` },
    packages:  { title: 'Packages',        subtitle: `${packages.filter(p => p.is_active).length} active · ${packages.length} total` },
    team:      { title: 'Team',            subtitle: `${users.length} members` },
  };
  const meta = PAGE_META[navKey] || PAGE_META.leads;

  return (
    <AppShell
      pageTitle={meta.title}
      subtitle={meta.subtitle}
      activeNav={navKey}
      onNavigate={handleSidebarNav}
      role="admin"
    >
      <div data-testid="ops-admin-page">
        {/* ──── LEADS / UNASSIGNED ──── */}
        {(tab === 'leads' || tab === 'unassigned') && (
          <>
            {/* Sub-tabs for All vs Unassigned — kept as a slim chip strip */}
            <div className="px-6 pt-5 flex items-center gap-1 border-b border-slate-200/0">
              <button
                onClick={() => { setStageFilter(''); setOwnerFilter(''); navigate('/ops/admin/leads'); }}
                data-testid="tab-leads"
                className={`text-[12px] px-3 py-1 rounded-md transition-colors ${tab === 'leads' ? 'bg-white border border-slate-200 text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'}`}
              >All leads <span className="text-slate-400 ml-1">{leads.length}</span></button>
              <button
                onClick={() => { setStageFilter(''); setOwnerFilter(''); navigate('/ops/admin/unassigned'); }}
                data-testid="tab-unassigned"
                className={`text-[12px] px-3 py-1 rounded-md transition-colors ${tab === 'unassigned' ? 'bg-white border border-slate-200 text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'}`}
              >Unassigned <span className="text-slate-400 ml-1">{unassigned.length}</span></button>
            </div>
            <LeadsTable
              leads={filteredLeads}
              stages={stages}
              interviewers={interviewers}
              searchTerm={searchTerm} setSearchTerm={setSearchTerm}
              stageFilter={stageFilter} setStageFilter={setStageFilter}
              ownerFilter={ownerFilter} setOwnerFilter={setOwnerFilter}
              onRowClick={(id) => setSelectedLead(id)}
              onAddLead={() => setShowAddLead(true)}
              onImportCsv={() => csvRef.current?.click()}
              getUserName={getUserName}
              hideOwnerFilter={tab === 'unassigned'}
            />
            <input ref={csvRef} type="file" accept=".csv" onChange={handleCsvImport} className="hidden" />
          </>
        )}

        {/* ──── KEEPSAKES ──── */}
        {tab === 'keepsakes' && (
          <KeepsakesGrid
            keepsakes={keepsakes}
            onAdd={() => { setEditingKeepsake(null); setShowKeepsakeModal(true); }}
            onEdit={(k) => { setEditingKeepsake(k); setShowKeepsakeModal(true); }}
          />
        )}

        {/* ──── EMAIL TEMPLATES ──── */}
        {tab === 'templates' && (
          <TemplatesTable
            templates={templates}
            onAdd={() => { setEditingTemplate(null); setShowTemplateModal(true); }}
            onEdit={(t) => { setEditingTemplate(t); setShowTemplateModal(true); }}
          />
        )}

        {/* ──── PACKAGES ──── */}
        {tab === 'packages' && (
          <PackagesTable
            packages={packages}
            onAdd={() => { setEditingPackage(null); setShowPackageModal(true); }}
            onEdit={(p) => { setEditingPackage(p); setShowPackageModal(true); }}
            onToggleActive={async (p) => {
              try {
                await axios.put(`${API}/ops/stripe-packages/${p.id}`, { is_active: !p.is_active }, getAuth());
                toast.success(`${p.package_name} ${!p.is_active ? 'activated' : 'deactivated'}`);
                fetchAll();
              } catch { toast.error('Could not toggle package'); }
            }}
            onRefresh={async (id) => {
              try {
                const { data } = await axios.post(`${API}/ops/stripe-packages/${id}/refresh`, {}, getAuth());
                if (data?.stripe_ok) {
                  const amt = data.amount != null ? `$${(data.amount/100).toFixed(2)} ${(data.currency||'usd').toUpperCase()}` : 'verified';
                  toast.success(`Refreshed from Stripe — ${amt}`);
                } else {
                  toast.error(`Stripe unreachable for ${data?.stripe_price_id || 'this Price ID'}. Verify the Price ID exists in the same Stripe account/mode as the API key.`);
                }
                fetchAll();
              } catch (e) {
                toast.error('Refresh failed: ' + (e?.response?.data?.detail || e.message));
              }
            }}
          />
        )}

        {/* ──── TEAM ──── */}
        {tab === 'team' && (
          <TeamTable
            users={users}
            interviewers={interviewers}
            onAdd={() => { setEditingUser(null); setShowUserModal(true); }}
            onEdit={(u) => { setEditingUser(u); setShowUserModal(true); }}
          />
        )}
      </div>

      {/* Modals */}
      {showUserModal && <UserModal user={editingUser} onClose={() => { setShowUserModal(false); setEditingUser(null); }} onSaved={fetchAll} />}
      {showAddLead && <AddLeadModal users={users} onClose={() => setShowAddLead(false)} onSaved={fetchAll} />}
      {showKeepsakeModal && <KeepsakeModal item={editingKeepsake} onClose={() => { setShowKeepsakeModal(false); setEditingKeepsake(null); }} onSaved={fetchAll} />}
      {showTemplateModal && <TemplateEditModal template={editingTemplate} onClose={() => { setShowTemplateModal(false); setEditingTemplate(null); }} onSaved={fetchAll} />}
      {showPackageModal && (
        <PackageModal
          package={editingPackage}
          onClose={() => { setShowPackageModal(false); setEditingPackage(null); }}
          onSave={async (data) => {
            if (editingPackage) {
              await axios.put(`${API}/ops/stripe-packages/${editingPackage.id}`, data, getAuth());
              toast.success('Package updated');
            } else {
              await axios.post(`${API}/ops/stripe-packages`, data, getAuth());
              toast.success('Package added');
            }
            fetchAll();
          }}
        />
      )}
      {selectedLead && <LeadDetailModal leadId={selectedLead} stages={stages} users={users} isAdmin={true} onClose={() => setSelectedLead(null)} onUpdated={fetchAll} />}
    </AppShell>
  );
}
